import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { resetPostazioneSelectedAndInfoPostazione } from '../../../redux/reducers/prenotazioneStepper-reducer';
import DettaglioPrenotazioneNewDialog from '../Dialogs/DettaglioPrenotazioneNewDialog';
import PlanimetriaPrenotazine from '../../Planimetria/PlanimetriaPrenotazione/PlanimetriaPrenotazione';
import LegendaComponent from '../../Planimetria/PlanimetriaPrenotazione/legenda/legendaMap/LegendaComponent';
import useBackGroundPlanimetria from '../../../Hooks/useBackgroundPlanimetria';
import { GetPostazioniWithStatusByIdStanza } from '../../../const/urlAction';
import { listaPostazioneIcon } from '../../../const/Planimetria/IconPlanimetria';
import { DISPONIBILE } from '../../../const/Planimetria/StatoPostazione';
import InlineLoader from '../../UI/spinner/InlineLoader';
import axios from 'axios';
import moment from "moment";
import '../styles/ViewPlanimetriaNew.css';

const urlBase = process.env.REACT_APP_URL;

export default function ViewPlanimetriaNew() {
    const { t } = useTranslation();
    const { selectedStanza, selectedDate, selectedStartTime, selectedEndTime } = useSelector(state => state.prenotazioneNew);
    const userDate = useSelector(state => state.userDate);
    const dispatch = useDispatch();
    const [selectedPostazione, setSelectedPostazione] = useState(null);
    const [isDettaglioOpen, setIsDettaglioOpen] = useState(false);
    const [elementiDalDb, setElementiDalDb] = useState([]);
    const [dettaglioStanza, setDettaglioStanza] = useState({});
    const [legendaInfoStanza, setLegendaInfoStanza] = useState([]);
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const [loading, setLoading] = useState(false);
    const hasLoadedOnce = useRef(false);
    const containerRef = useRef(null);
    const [containerDimensions, setContainerDimensions] = useState(null);

    // Misura il container reale con ResizeObserver per eliminare scrollbar inutili in desktop
    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width > 0 && height > 0) {
                    setContainerDimensions({
                        larghezza: `${Math.floor(width)}px`,
                        altezza: `${Math.floor(height)}px`,
                    });
                }
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const handleRefresh = () => {
        dispatch(resetPostazioneSelectedAndInfoPostazione());
        setSelectedPostazione(null);
        setReloadTrigger(prev => prev + 1);
    };

    useEffect(() => {
        let isMounted = true;

        (async () => {
            if (selectedStanza?.id) {
                setLoading(true);
                const requestBody = {
                    idUtentePrenotazione: userDate?.idUtente || "",
                    dataPrenotazione: moment(selectedDate).format(),
                    oraInizioPrenotazione: moment(selectedStartTime).format("HH:mm"),
                    oraFinePrenotazione: moment(selectedEndTime).format("HH:mm"),
                    dataFineRipetizioni: moment(selectedDate).format(),
                    giorniRipetizione: [],
                    IdStanza: selectedStanza.id,
                };
                const { method, action } = GetPostazioniWithStatusByIdStanza;

                try {
                    const response = await axios({
                        method: method,
                        url: urlBase + action,
                        data: requestBody,
                        headers: { "Content-Type": "application/json" },
                    });

                    if (isMounted) {
                        setLegendaInfoStanza(response.data.result.legendaInfo);
                        setDettaglioStanza(response.data.result.dettaglio);
                        setElementiDalDb(response.data.result.listaEntita);
                        hasLoadedOnce.current = true;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    if (isMounted) setLoading(false);
                }
            }
        })();
        return () => { isMounted = false; };
    }, [selectedStanza, selectedDate, selectedStartTime, selectedEndTime, reloadTrigger]);

    let imageBackground = useBackGroundPlanimetria({ idStanza: selectedStanza?.id });

    const handlePostazioneClick = (event, id, index, posto, status) => {
        if (status === DISPONIBILE) {
            const postazioneData = elementiDalDb.find(p => p.id === id);
            setSelectedPostazione(postazioneData || { id });
            setIsDettaglioOpen(true);
        }
        return { index, posto };
    };

    const showInitialLoading = loading && (!hasLoadedOnce.current || !imageBackground);

    const mapContainerStyle = {
        opacity: loading && hasLoadedOnce.current ? 0.7 : 1,
    };

    return (
        <div className="view-planimetria-new-root">
            <div
                ref={containerRef}
                className={`view-planimetria-new-map-container${loading && hasLoadedOnce.current ? ' view-planimetria-new-map-container--reloading' : ''}`}
                style={mapContainerStyle}
            >
                {showInitialLoading ? (
                    <InlineLoader message={t("load_planimetry")} />
                ) : imageBackground && elementiDalDb.length > 0 ? (
                    <PlanimetriaPrenotazine
                        dettaglioSfondoPlanimetria={dettaglioStanza}
                        elementiDalDb={elementiDalDb}
                        typePage="stanza"
                        imageBackground={imageBackground}
                        IconsToCreate={listaPostazioneIcon}
                        actionOnClickButton={handlePostazioneClick}
                        disableAutoSelect={true}
                        containerDimensionsOverride={containerDimensions}
                    />
                ) : !loading && (
                    <div className="view-planimetria-new-empty">
                        <Typography variant="body2">{t("no_elements_available")}</Typography>
                    </div>
                )}
            </div>

            {legendaInfoStanza && legendaInfoStanza.length > 0 && (
                <div className="view-planimetria-new-legenda-wrapper">
                    <LegendaComponent infoMap={legendaInfoStanza} />
                </div>
            )}

            <DettaglioPrenotazioneNewDialog
                open={isDettaglioOpen}
                onClose={() => setIsDettaglioOpen(false)}
                postazione={selectedPostazione}
                onBookingSuccess={handleRefresh}
            />
        </div>
    );
}